import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { loadPlacesLibrary } from '../utils/loadGoogleMaps';

/**
 * Business-name input backed by Google Places Autocomplete (Places API New).
 *
 * A sibling to `AddressAutocomplete`: same session-token / debounce / portal
 * dropdown / graceful-degradation machinery, but it searches for
 * establishments (businesses) rather than addresses, and — on selection —
 * fetches the business's contact details so the caller can pre-fill a form.
 *
 * Behaviour:
 *  - Free typing calls `onChange(text)` (the typed company name).
 *  - Suggestions are limited to establishments and, when a `locationBias` is
 *    supplied, biased toward the landlord's service area so local vendors
 *    surface first.
 *  - Selecting a suggestion resolves the place and calls `onSelect` with
 *    `{ placeId, name, address, phone, website, primaryType, types, lat, lng }`.
 *    Any field Google doesn't provide is null/empty.
 *  - If the Maps script/key is unavailable it silently degrades to a plain text
 *    input, so manual entry always works.
 *
 * `locationBias` (optional): `{ center: { lat, lng }, radius }` where radius is
 * in meters (Google clamps this to <= 50000). When omitted, suggestions are
 * nationwide.
 */
const DEBOUNCE_MS = 250;

const BusinessAutocomplete = ({
  value,
  onChange,
  onSelect = () => {},
  locationBias = null,
  id,
  required,
  placeholder,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dropdownRect, setDropdownRect] = useState(null);
  // True while the selected place's details are being fetched over the network.
  const [resolving, setResolving] = useState(false);

  const placesLibRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  // Keep the latest bias in a ref so fetchSuggestions stays referentially
  // stable while still reading the freshest value.
  const locationBiasRef = useRef(locationBias);
  useEffect(() => {
    locationBiasRef.current = locationBias;
  }, [locationBias]);

  useEffect(() => {
    let cancelled = false;
    loadPlacesLibrary()
      .then((places) => {
        if (!cancelled) placesLibRef.current = places;
      })
      .catch(() => {
        // Degrade gracefully to a plain text input.
        placesLibRef.current = null;
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const ensureSessionToken = useCallback(() => {
    const places = placesLibRef.current;
    if (!places) return null;
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new places.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  }, []);

  const fetchSuggestions = useCallback(
    async (input) => {
      const places = placesLibRef.current;
      if (!places || !input || input.trim().length < 3) {
        setSuggestions([]);
        setOpen(false);
        return;
      }
      try {
        // NOTE: the Maps JS SDK does NOT accept 'establishment' in
        // includedPrimaryTypes (it only allows specific Table A/B types or
        // (regions)/(cities)); passing it throws InvalidValueError. So we leave
        // types unrestricted and softly prefer establishment predictions below.
        const request = {
          input,
          sessionToken: ensureSessionToken(),
        };
        const bias = locationBiasRef.current;
        if (
          bias &&
          bias.center &&
          Number.isFinite(bias.center.lat) &&
          Number.isFinite(bias.center.lng) &&
          Number.isFinite(bias.radius) &&
          bias.radius > 0
        ) {
          // The Maps JS SDK expects a CircleLiteral ({ center, radius })
          // directly here — NOT the REST-style { circle: {...} } wrapper.
          request.locationBias = {
            center: { lat: bias.center.lat, lng: bias.center.lng },
            radius: Math.min(bias.radius, 50000),
          };
        }

        const { suggestions: results } =
          await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
        const predictions = (results || [])
          .map((s) => s.placePrediction)
          .filter(Boolean);
        // Prefer businesses, but fall back to all predictions if none are
        // tagged as establishments so the field never goes silently empty.
        const businesses = predictions.filter((p) =>
          (p.types || []).includes('establishment')
        );
        const shown = businesses.length ? businesses : predictions;
        setSuggestions(shown);
        setActiveIndex(-1);
        setOpen(shown.length > 0);
      } catch (err) {
        // Network / quota / restriction errors: keep manual entry working.
        console.error('Business autocomplete fetch failed', err);
        setSuggestions([]);
        setOpen(false);
      }
    },
    [ensureSessionToken]
  );

  const handleInputChange = (e) => {
    const text = e.target.value;
    onChange(text);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(text), DEBOUNCE_MS);
  };

  const handleSelect = useCallback(
    async (prediction) => {
      setOpen(false);
      setSuggestions([]);
      setResolving(true);
      try {
        const place = prediction.toPlace();
        await place.fetchFields({
          fields: [
            'id',
            'displayName',
            'formattedAddress',
            'nationalPhoneNumber',
            'internationalPhoneNumber',
            'websiteURI',
            'primaryType',
            'types',
            'location',
          ],
        });
        const name =
          place.displayName ||
          prediction.mainText?.text ||
          prediction.text?.text ||
          '';
        onChange(name);
        onSelect({
          placeId: place.id,
          name,
          address: place.formattedAddress || '',
          phone: place.nationalPhoneNumber || place.internationalPhoneNumber || '',
          website: place.websiteURI || '',
          primaryType: place.primaryType || '',
          types: place.types || [],
          lat: place.location ? place.location.lat() : null,
          lng: place.location ? place.location.lng() : null,
        });
      } catch (err) {
        // Fall back to the prediction's display text if details fetch fails.
        console.error('Business details fetch failed', err);
        const name = prediction.mainText?.text || prediction.text?.text || '';
        onChange(name);
        onSelect({
          placeId: prediction.placeId,
          name,
          address: '',
          phone: '',
          website: '',
          primaryType: '',
          types: [],
          lat: null,
          lng: null,
        });
      } finally {
        // A new session begins after a selection is resolved.
        sessionTokenRef.current = null;
        setResolving(false);
      }
    },
    [onChange, onSelect]
  );

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const updateDropdownRect = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setDropdownRect({ top: rect.bottom, left: rect.left, width: rect.width });
  }, []);

  // Keep the portaled dropdown aligned with the input while it is open. We
  // listen with `capture` so scrolling inside the modal's scroll container
  // (not just the window) repositions the dropdown too.
  useEffect(() => {
    if (!open) return undefined;
    updateDropdownRect();
    window.addEventListener('scroll', updateDropdownRect, true);
    window.addEventListener('resize', updateDropdownRect);
    return () => {
      window.removeEventListener('scroll', updateDropdownRect, true);
      window.removeEventListener('resize', updateDropdownRect);
    };
  }, [open, updateDropdownRect]);

  // Close the dropdown on outside click. The dropdown is portaled to
  // document.body, so it lives outside containerRef and must be checked too.
  useEffect(() => {
    const onDocClick = (e) => {
      const inContainer = containerRef.current && containerRef.current.contains(e.target);
      const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!inContainer && !inDropdown) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Input
          id={id}
          required={required}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder}
          autoComplete="off"
          disabled={resolving}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-busy={resolving}
          className={resolving ? 'pr-9' : undefined}
        />
        {resolving && (
          <Loader2
            className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-[#64748B]"
            aria-hidden="true"
          />
        )}
      </div>

      {open &&
        suggestions.length > 0 &&
        dropdownRect &&
        ReactDOM.createPortal(
          <ul
            ref={dropdownRef}
            className="pointer-events-auto fixed z-50 mt-1 max-h-60 overflow-y-auto rounded-md border border-[#E2E8F0] bg-white py-1 shadow-lg"
            style={{
              top: dropdownRect.top,
              left: dropdownRect.left,
              width: dropdownRect.width,
            }}
            role="listbox"
          >
            {suggestions.map((prediction, idx) => {
              const main = prediction.mainText?.text || prediction.text?.text || '';
              const secondary = prediction.secondaryText?.text || '';
              return (
                <li
                  key={prediction.placeId || idx}
                  role="option"
                  aria-selected={idx === activeIndex}
                  // mousedown (not click) fires before the input blur, so the
                  // selection isn't lost to the outside-click handler.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(prediction);
                  }}
                  onMouseEnter={() => setActiveIndex(idx)}
                  className={`cursor-pointer px-3 py-2 text-sm ${
                    idx === activeIndex ? 'bg-[#F1F5F9]' : 'hover:bg-[#F8FAFC]'
                  }`}
                >
                  <span className="text-[#030712]">{main}</span>
                  {secondary && (
                    <span className="text-[#64748B]"> {secondary}</span>
                  )}
                </li>
              );
            })}
          </ul>,
          document.body
        )}
    </div>
  );
};

export default BusinessAutocomplete;
